/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Lib dependencies
import {
	ActionIcon,
	Drawer,
	Flex,
	Stack,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Styles
import classes from './Filter.module.css';

// UI local components
import { Button, Title } from '@components/v2/ui';

// Icons
import { IconX } from '@tabler/icons-react';

// Utils
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import useStore from '@store/index';
import {
	cloneDeep,
	convertArrayToLabelValue,
} from '@utils/functions';
import { useSites } from 'shared/api/get-sites';

/* -------------------------------------------------------------------------- */
/*                                   Types                                     */
/* -------------------------------------------------------------------------- */

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	'location.countryCode'?: { $in: string[] };
	organization?: string[];
	project?: string[];
	site?: string[];
	quantityCheck?: string[];
	status?: string[];
	sellPrice_europertco2eq?: RangeFilter;
	'quantity.remaining_aco2'?: RangeFilter;
	[key: string]: unknown;
	startDate?: {
		$gte: string | Date;
		$lt: string | Date;
	};
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	/* ******************************** HOOKS ******************************* */

	const { t } = useTranslation();

	const {
		setProductionsPage,
		setSelectedProductionsForMint,
	} = useStore();

	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	const { data: sites } = useSites({
		project: value.project || [],
	});

	/* ******************************** HELPERS ******************************* */

	const handleReset = () => {
		const resetParams = cloneDeep(value ?? {});

		delete resetParams.site;
		setSelectedProductionsForMint([]);
		setQueryParams(resetParams);
		onApplyFilters(resetParams);
		onClose();
	};

	const handleApplyFilters = () => {
		setProductionsPage(1);
		setSelectedProductionsForMint([]);
		const finalFilters = {
			...value,
			...queryParams,
		};

		onApplyFilters(finalFilters);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));
		}
	}, [opened, value]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			{/* Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
				pb={'var(--mantine-spacing-xl)'}
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>

				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>

			{/* Drawer content */}
			<Flex
				flex={1}
				direction={'column'}
				justify="space-between"
			>
				<Stack gap={'var(--mantine-spacing-sm)'}>
					<MultiSelect
						placeholder={t('global.sites')}
						w={'100%'}
						comboboxProps={{ withinPortal: false }}
						data={convertArrayToLabelValue(sites)}
						clearable
						size="lg"
						value={queryParams.site ?? []}
						searchable
						onChange={(value) => {
							if (value.length === 0) {
								const _cloneQueryParams =
									cloneDeep(queryParams);
								delete _cloneQueryParams['site'];
								setQueryParams(_cloneQueryParams);
								return;
							}
							setQueryParams({
								...queryParams,
								site: value,
							});
						}}
					/>
				</Stack>

				{/* Buttons */}
				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
				>
					<Button
						fullWidth
						variant={'outline'}
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						onClick={handleApplyFilters}
						fullWidth
						variant={'primary'}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
